import React from "react";
import CustomDialog from "./common/CustomDialog";
import CustomLabel from "./common/CustomLabel";
import { COLORS } from "../assets/theme";
import CustomTextField from "./common/CustomTextField";
import { Box } from "@mui/system";

interface ConfirmAmountsDialogProps {
  open: boolean;
  onClose: () => void;
  type: "confirm";
  isUploading: boolean;
  errors: { [key: string]: boolean };
  dialogData: any;
  onChange: (field: string, value: string | number) => void;
  onConfirm: () => void;
}

const logAmountsAndDifference = (dialogData: any) => {
  const amount = Number(dialogData.amount) || 0;
  const confirmedAmount = Number(dialogData.confirmedAmount) || 0;
  const difference = confirmedAmount - amount;
  console.log('amount:', amount);
  console.log('confirmedAmount:', confirmedAmount);
  console.log('difference (confirmedAmount - amount):', difference);
};

const ConfirmAmountsDialog: React.FC<ConfirmAmountsDialogProps> = ({
  open,
  onClose,
  isUploading,
  errors,
  dialogData,
  onChange,
  type,
  onConfirm,
}) => {
  const amount = Number(dialogData.amount) || 0;
  const confirmedAmount = Number(dialogData.confirmedAmount) || 0;
  const difference = confirmedAmount - amount;
  const absDiff = Math.abs(difference);
  const absDiffFormatted = Number.isInteger(absDiff) ? absDiff : absDiff.toFixed(3);

  let percent = amount !== 0 ? Math.abs((difference / amount) * 100) : 0;
  percent = Math.min(percent, 100);

  let buttonBg = COLORS.successGreen;
  if (percent >= 20) {
    buttonBg = COLORS.RED;
  } else if (percent >= 10) {
    buttonBg = COLORS.WarningOrange;
  }

  const diffLabel =
    percent === 0
      ? `NO DIFF (${absDiffFormatted}, ${percent.toFixed(0)}%)`
      : difference > 0
        ? `The confirmed amount is higher by ${absDiffFormatted} (${percent.toFixed(0)}%)`
        : `The confirmed amount is lower by ${absDiffFormatted} (${percent.toFixed(0)}%)`;

  const isConfirmedAmountEmpty =
    dialogData.confirmedAmount === undefined ||
    dialogData.confirmedAmount === null ||
    dialogData.confirmedAmount === "";
  const isConfirmedRefEmpty =
    dialogData.confirmedRef === undefined ||
    dialogData.confirmedRef === null ||
    dialogData.confirmedRef === "";
  const confirmLabel = isConfirmedAmountEmpty
    ? "Confirm"
    : `Confirm | ${diffLabel}`;
  const canConfirm =
    !isUploading && !isConfirmedAmountEmpty && !isConfirmedRefEmpty;

  React.useEffect(() => {
    logAmountsAndDifference(dialogData);
  }, [dialogData.amount, dialogData.confirmedAmount]);

  return (
    <CustomDialog
      open={open}
      type={type}
      onClose={onClose}
      title="Confirm"
      fields={[
        {
          type: "custom",
          component: (
            <Box
              width="100%"
              display="flex"
              gap={4}
              flexDirection={{ xs: "column", md: "row" }}
            >
              <Box flex={1} width="100%">
                <CustomLabel
                  text="Generated Amount"
                  variant="subtitle1"
                  fontWeight={600}
                  marginBottom={1}
                />
                <CustomTextField
                  label="Ref Name"
                  placeholder="Ref"
                  fullWidth
                  bgColor={COLORS.WhiteBackground}
                  value={dialogData.ref ?? ""}
                  disabled
                  onChange={(e) => {
                    console.log("new value:", e.target.value);
                  }}
                  error={!!errors.ref1}
                  helperText={errors.ref1 ? "Ref Name is required" : ""}
                />
                <CustomTextField
                  type="number"
                  label="Amount"
                  placeholder="Amount"
                  fullWidth
                  disabled
                  bgColor={COLORS.WhiteBackground}
                  value={dialogData.amount ?? ""}
                  onChange={(e) => onChange("amount1", e.target.value)}
                  error={!!errors.amount1}
                  helperText={errors.amount1 ? "Amount is required" : ""}
                  sx={{ mb: 2 }}
                />
              </Box>
              <Box flex={1} width="100%">
                <CustomLabel
                  text="Confirmed Amount"
                  variant="subtitle1"
                  fontWeight={600}
                  marginBottom={1}
                />
                <CustomTextField
                  label="Ref"
                  placeholder="Ref"
                  fullWidth
                  highlightLabelStar={true}
                  bgColor={COLORS.WhiteBackground}
                  value={dialogData.confirmedRef ?? ""}
                  onChange={(e) => onChange("confirmedRef", e.target.value)}
                  error={!!errors.confirmedRef}
                  helperText={errors.confirmedRef ? "Ref Name is required" : ""}
                />
                <CustomTextField
                  type="number"
                  label="Amount"
                  placeholder="Amount"
                  fullWidth
                  highlightLabelStar={true}
                  bgColor={COLORS.WhiteBackground}
                  value={dialogData.confirmedAmount ?? ""}
                  onChange={(e) => onChange("confirmedAmount", e.target.value)}
                  error={!!errors.confirmedAmount}
                  helperText={errors.confirmedAmount ? "Amount is required" : ""}
                  sx={{ mb: 2 }}
                />
              </Box>
            </Box>
          ),
        },
      ]}
      buttons={[
        {
          label: confirmLabel,
          onClick: onConfirm,
          variant: "contained",
          disabled: !canConfirm,
          bgColor: canConfirm ? buttonBg : COLORS.GOLD,
          textColor: COLORS.White,
          bold: canConfirm ? "bold" : undefined,
          borderRadius: "12px",
        },
      ]}
      mode="add"
    />
  );
};

export default ConfirmAmountsDialog;
